// pages/DashboardPage.jsx
// Dashboard de gestion du vocabulaire

import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import './DashboardPage.css';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const langues = [
    { code: 'fr', label: 'Francais', flag: '🇫🇷' },
    { code: 'es', label: 'Espagnol', flag: '🇪🇸' },
    { code: 'en', label: 'Anglais', flag: '🇬🇧' },
  ];

  const types = [
    { value: 'nom', label: 'Nom' },
    { value: 'adjectif', label: 'Adjectif' },
    { value: 'adverbe', label: 'Adverbe' },
    { value: 'pronom', label: 'Pronom' },
    { value: 'expression', label: 'Expression' },
    { value: 'verbe', label: 'Verbe' },
  ];

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/dashboard/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const getLangueInfo = (code) => {
    return langues.find((l) => l.code === code) || { code, label: code, flag: '' };
  };

  const getTypeLabel = (value) => {
    const type = types.find((t) => t.value === value);
    return type ? type.label : value;
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <h1>Tableau de bord</h1>
        <div className="loading">Chargement des statistiques...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <h1>Tableau de bord</h1>
        <div className="error-message">{error}</div>
        <button className="btn-refresh" onClick={fetchStats}>
          <RefreshCw size={18} /> Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Tableau de bord - Vocabulaire</h1>
        <button className="btn-refresh" onClick={fetchStats}>
          <RefreshCw size={18} /> Actualiser
        </button>
      </div>

      {/* Section par langue */}
      <section className="section-langues">
        <h2>Statistiques par langue</h2>
        <div className="langues-grid">
          {langues.map((langue) => {
            const data = stats?.parLangue?.[langue.code] || {};
            return (
              <div key={langue.code} className="langue-card">
                <div className="langue-header">
                  <span className="langue-flag">{langue.flag}</span>
                  <span className="langue-label">Mots {langue.label}</span>
                </div>

                <div className="langue-stats">
                  <div className="stat-row">
                    <span className="stat-label">En attente</span>
                    <span className={`stat-value ${data.enAttente > 0 ? 'warning' : ''}`}>{data.enAttente || 0}</span>
                  </div>
                  <div className="stat-row total">
                    <span className="stat-label">Total mots</span>
                    <span className="stat-value">{data.total || 0}</span>
                  </div>
                </div>

                <div className="langue-types">
                  <h4>Par type</h4>
                  {types.map((type) => (
                    <div key={type.value} className="type-row">
                      <span className="type-label">{type.label}</span>
                      <span className="type-value">{data.parType?.[type.value] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section mots incomplets */}
      <section className="section-incomplets">
        <h2>
          <AlertTriangle size={20} /> Mots incomplets par langue et type
        </h2>

        {stats?.incomplets?.length === 0 ? (
          <div className="no-incomplets">Aucun mot incomplet detecte</div>
        ) : (
          <div className="incomplets-table">
            <div className="table-header">
              <div className="col-langue">Langue</div>
              <div className="col-type">Type</div>
              <div className="col-count">Sans pluriel</div>
              <div className="col-count">Sans genre</div>
              <div className="col-total">Total incomplets</div>
            </div>

            {stats?.incomplets?.map((item, index) => {
              const langueInfo = getLangueInfo(item.langue);
              const total = item.sansPluriel + item.sansGenre;
              return (
                <div key={index} className="table-row">
                  <div className="col-langue">
                    <span className="flag">{langueInfo.flag}</span>
                    <span className="code">{item.langue.toUpperCase()}</span>
                  </div>
                  <div className="col-type">{getTypeLabel(item.type)}</div>
                  <div className="col-count">
                    {item.sansPluriel > 0 ? <span className="count warning">{item.sansPluriel}</span> : <span className="count ok">0</span>}
                  </div>
                  <div className="col-count">
                    {item.sansGenre > 0 ? <span className="count warning">{item.sansGenre}</span> : <span className="count ok">0</span>}
                  </div>
                  <div className="col-total">
                    <span className={`count ${total > 10 ? 'danger' : total > 0 ? 'warning' : 'ok'}`}>{total}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
